import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
// Start-Karten nutzen eigene, verkleinerte WebP-Varianten (1280 px, q85).
// Die Originale bleiben unangetastet — sie sind die Heros der Unterseiten.
import flaecheImg from "@/assets/home/hero-flaecheneigentuemer-new-card.webp";
import netzImg from "@/assets/home/hero-netzbetreiber-card.webp";
import kommunenImg from "@/assets/home/hero-kommunen-card.webp";

interface CardProps {
  image: string;
  title: string;
  kpiValue: string;
  kpiLabel: string;
  desc: string;
  ctaLabel: string;
  to: string;
}

const Card = ({ image, title, kpiValue, kpiLabel, desc, ctaLabel, to }: CardProps) => (
  <Link
    to={to}
    className="group relative block overflow-hidden rounded-2xl h-[440px] shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
  >
    <img
      src={image}
      alt={title}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-ci-forest via-ci-forest/70 to-ci-forest/20 group-hover:from-ci-forest group-hover:via-ci-forest/80 transition-colors" />
    <div className="relative h-full flex flex-col justify-end p-6 text-white">
      <h3 className="text-2xl font-bold mb-3">{title}</h3>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="text-4xl font-bold text-primary">{kpiValue}</span>
        <span className="text-sm text-white/85">{kpiLabel}</span>
      </div>
      <p className="text-sm text-white/85 mb-4 leading-relaxed">{desc}</p>
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-white group-hover:gap-2 transition-all">
        {ctaLabel} <ArrowRight className="w-4 h-4" />
      </span>
    </div>
  </Link>
);

const WhoWeWorkWithSection = () => {
  const { t } = useLanguage();

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container-custom">
        <p className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-primary mb-3">
          {t("home.who.eyebrow")}
        </p>
        <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-ci-forest mb-12 max-w-4xl leading-[1.1]">
          {t("home.who.title")}
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          <Card
            image={flaecheImg}
            title={t("home.who.card1.title")}
            kpiValue={t("home.who.card1.kpiValue")}
            kpiLabel={t("home.who.card1.kpiLabel")}
            desc={t("home.who.card1.desc")}
            ctaLabel={t("home.who.card1.cta")}
            to="/flaecheneigentuemer"
          />
          <Card
            image={netzImg}
            title={t("home.who.card2.title")}
            kpiValue={t("home.who.card2.kpiValue")}
            kpiLabel={t("home.who.card2.kpiLabel")}
            desc={t("home.who.card2.desc")}
            ctaLabel={t("home.who.card2.cta")}
            to="/netzbetreiber"
          />
          <Card
            image={kommunenImg}
            title={t("home.who.card3.title")}
            kpiValue={t("home.who.card3.kpiValue")}
            kpiLabel={t("home.who.card3.kpiLabel")}
            desc={t("home.who.card3.desc")}
            ctaLabel={t("home.who.card3.cta")}
            to="/kommunen"
          />
        </div>
      </div>
    </section>
  );
};

export default WhoWeWorkWithSection;
